import { UserIncentive } from '@tovala/browser-apis-combinedapi'
import InformationIcon from 'components/common/icons/InformationIcon'
import { ReactNode } from 'react'
import {
  formatMealDiscountValue,
  getAppliesToOrdersText,
  getMealDiscountText,
  hasFreeShipping,
  hasMealDiscount,
} from 'utils/incentives'

const CreditsBanner = ({
  activeIncentives = [],
  isFreeDessertCustomer = false,
  totalCreditsCents,
  onClickBanner,
}: {
  activeIncentives?: UserIncentive[]
  isFreeDessertCustomer?: boolean
  totalCreditsCents: number
  onClickBanner?: () => void
}) => {
  const nextOrdersIncentives = activeIncentives.filter(
    (incentive) => incentive.applicability === 'nextOrders',
  )

  if (
    totalCreditsCents === 0 &&
    !isFreeDessertCustomer &&
    nextOrdersIncentives.length === 0
  ) {
    return null
  }

  const content = getCreditsBannerContent({
    totalCreditsCents,
    isFreeDessertCustomer,
    incentives: nextOrdersIncentives,
  })

  return (
    <BannerWrapper onClick={onClickBanner}>
      <span>{content.message}</span>
      {content.withMoreInfo && (
        <span className="inline-block size-7 md:size-6">
          <InformationIcon />
        </span>
      )}
    </BannerWrapper>
  )
}

export default CreditsBanner

const BannerWrapper = ({
  children,
  onClick,
}: {
  children: ReactNode
  onClick?: () => void
}) => {
  if (onClick) {
    return (
      <button
        className="flex w-full items-center justify-center bg-[#398356] p-4 text-center text-white md:p-3 md:text-k/13_120"
        onClick={onClick}
      >
        {children}
      </button>
    )
  }

  return (
    <div className="flex items-center justify-center bg-[#398356] p-4 text-center text-white md:p-3 md:text-k/13_120">
      {children}
    </div>
  )
}

function getCreditsBannerContent({
  totalCreditsCents,
  isFreeDessertCustomer,
  incentives,
}: {
  totalCreditsCents: number
  isFreeDessertCustomer: boolean
  incentives: UserIncentive[]
}) {
  const atLeastTwoApply =
    [
      totalCreditsCents > 0,
      isFreeDessertCustomer,
      incentives.length > 0,
    ].filter((item) => item === true).length >= 2 || incentives.length > 1

  let content = {
    message: '',
    withMoreInfo: false,
  }

  if (atLeastTwoApply) {
    content = {
      message: 'Multiple discounts active',
      withMoreInfo: true,
    }
  } else if (isFreeDessertCustomer) {
    content = {
      message: 'Add a dessert and get it free—for life!',
      withMoreInfo: false,
    }
  } else if (totalCreditsCents > 0) {
    content = {
      message: `${formatMealDiscountValue(totalCreditsCents)} off applies to your next order`,
      withMoreInfo: false,
    }
  } else if (incentives.length === 1) {
    content = {
      message: getIncentiveMessage({ incentive: incentives[0] }),
      withMoreInfo: false,
    }
  }

  return content
}

function getIncentiveMessage({ incentive }: { incentive: UserIncentive }) {
  const { titleOverride } = incentive
  const appliesToOrders = getAppliesToOrdersText(incentive)

  let message = ''

  if (titleOverride && incentive.totalOrdersCount === null) {
    message = `${titleOverride} applies to ${appliesToOrders}`
  } else if (titleOverride) {
    message = titleOverride
  } else if (hasFreeShipping(incentive) && hasMealDiscount(incentive)) {
    message = `Free shipping and ${getMealDiscountText(incentive)} applies to ${appliesToOrders}`
  } else if (
    hasFreeShipping(incentive) &&
    incentive.totalOrdersCount === null
  ) {
    message = `Free shipping for life applies to ${appliesToOrders}`
  } else if (hasFreeShipping(incentive)) {
    message = `Free shipping applies to ${appliesToOrders}`
  } else if (hasMealDiscount(incentive)) {
    message = `${getMealDiscountText(incentive)} applies to ${appliesToOrders}`
  }
  return message
}
